import { Box, Divider, Heading, Text } from "@chakra-ui/react";
import { FC, useEffect, useRef } from "react";
import Markdown from "react-markdown";
import { Link, Navigate, useParams } from "react-router";
import remarkGfm from "remark-gfm";

import { PageSection } from "../../components";

import { BLOG_POSTS } from "./consts/posts";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("nl-NL", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const NO_MARGIN_CLASS_NAME = "no-top-margin";

/** Renders a simple markdown-like content string as paragraphs and headings. */
const BlogContent: FC<{ content: string }> = ({ content }) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Keep the first rendered markdown block flush with the container.
  useEffect(() => {
    if (!contentRef.current) return;
    const children = contentRef.current.querySelectorAll("*");
    if (!children.length) return;
    children.forEach((element) => element.classList.remove(NO_MARGIN_CLASS_NAME));
    children[0].classList.add(NO_MARGIN_CLASS_NAME);
  }, [content]);

  return (
    <Box
      ref={contentRef}
      sx={{
        "p, hr": { marginTop: "8px !important" },
        h2: { marginTop: "32px !important" },
        [`.${NO_MARGIN_CLASS_NAME}`]: {
          marginTop: "0 !important",
        },
        "ul, ol": { paddingLeft: "1.5em" },
        th: { textAlign: "start" },
      }}
    >
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          h2: ({ children }) => (
            <Heading as="h2" fontSize="xl" color="text" mt={8} mb={4}>
              {children}
            </Heading>
          ),
          hr: () => <Divider my={8} />,
          p: ({ children }) => (
            <Text fontWeight="400" color="gray.700" lineHeight="tall">
              {children}
            </Text>
          ),
          strong: ({ children }) => (
            <Box as="strong" fontWeight="700">
              {children}
            </Box>
          ),
          /* Open links in new tab */
          a: ({ children, ...props }) => (
            <a target="_blank" style={{ textDecoration: "underline" }} {...props}>
              {children}
            </a>
          ),
        }}
      >
        {content}
      </Markdown>
    </Box>
  );
};

export const LinkToBlog: FC = () => {
  return (
    <Link to="/public/blog" style={{ textDecoration: "none" }}>
      <Text fontSize="sm" fontWeight="700" color="primary">
        ← Terug naar de blog
      </Text>
    </Link>
  );
};

export const BlogPost: FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | BROSTAR`;
      return () => {
        document.title = "BROSTAR - BRO Lever en correctie Software voor Bronhouders";
      };
    }
  }, [post]);

  if (!post) {
    return <Navigate to="/public/blog" replace />;
  }

  return (
    <Box>
      <Box bg="accentLight" px={{ base: 6, md: 20 }} py={{ base: 14, md: 20 }}>
        <Box maxW="800px" mx="auto">
          <Text fontSize="sm" fontWeight="400" color="gray.500" mb={3}>
            {formatDate(post.date)}
          </Text>
          <Heading as="h1" fontSize={{ base: "2xl", md: "4xl" }} color="text" lineHeight="short">
            {post.title}
          </Heading>
        </Box>
      </Box>

      <PageSection>
        <Box maxW="800px" mx="auto">
          <Text fontWeight="400" fontSize="lg" color="gray.600" lineHeight="tall" mb={8}>
            {post.excerpt}
          </Text>
          <Divider mb={8} />
          <BlogContent content={post.content} />
          <Divider mt={12} mb={8} />
          <LinkToBlog />
        </Box>
      </PageSection>
    </Box>
  );
};
